<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { CheckCircle2, Clock3, Route, XCircle } from "lucide-vue-next";
import { api } from "@/lib/api";
import { session } from "@/lib/session";
import type { Journey } from "@/types";

type Tab = "active" | "pending" | "approved" | "rejected";
const router = useRouter(); const tab = ref<Tab>("active"); const journeys = ref<Journey[]>([]);
const loading = ref(true); const error = ref("");
const tabs: { key: Tab; label: string }[] = [{key:"active",label:"进行中"},{key:"pending",label:"待审核"},{key:"approved",label:"已合格"},{key:"rejected",label:"已驳回"}];
const visible = computed(() => journeys.value.filter((item) => tab.value === "active"
  ? ["claimed","in_progress"].includes(item.status)
  : tab.value === "rejected" ? ["rejected","expired"].includes(item.status) : item.status === tab.value));

function statusLabel(status: Journey["status"]) {
  return ({claimed:"待起点打卡",in_progress:"执行中",pending:"待审核",approved:"已合格",rejected:"已驳回",expired:"已过期"} as const)[status];
}
function statusClass(status: Journey["status"]) {
  if (status === "approved") return "pill-success"; if (["rejected","expired"].includes(status)) return "pill-danger";
  if (status === "pending") return "pill-warning"; return "";
}
async function load() {
  try { journeys.value = await api.get<Journey[]>("/journeys", session.driverToken); }
  catch (cause) { error.value = cause instanceof Error ? cause.message : "加载失败"; }
  finally { loading.value = false; }
}
onMounted(load);
</script>

<template>
  <section class="stack">
    <div class="segmented task-tabs"><button v-for="item in tabs" :key="item.key" :class="{active:tab===item.key}" @click="tab=item.key">{{ item.label }}</button></div>
    <div v-if="loading" class="card state-box">正在加载任务记录…</div>
    <div v-else-if="error" class="error-banner">{{ error }}</div>
    <div v-else-if="!visible.length" class="card state-box"><Route :size="30" /><div>这里还没有任务</div></div>
    <article v-for="item in visible" :key="item.id" class="card my-task" @click="router.push(`/journeys/${item.id}`)">
      <div class="row between"><b>{{ item.taskTitle }}</b><span class="pill" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span></div>
      <div class="route-line"><span class="route-dot" />{{ item.taskOriginName }}<span class="route-dash" /><span class="route-dot end" />{{ item.taskDestinationName }}</div>
      <div class="row between small muted" style="margin-top:13px"><span>{{ item.taskCode }} · {{ item.taskDistanceKm }}km</span><span v-if="item.taskRewardCents" class="money">{{ (item.taskRewardCents/100).toFixed(0) }}</span></div>
      <div v-if="item.status === 'pending'" class="result-line warning"><Clock3 :size="15" />提交于 {{ new Date(item.submittedAt || '').toLocaleString('zh-CN') }}</div>
      <div v-if="item.status === 'approved'" class="result-line success"><CheckCircle2 :size="15" />百公里能耗 {{ item.actualConsumption?.toFixed(2) }} {{ item.energyUnit }}/100km</div>
      <div v-if="item.status === 'rejected'" class="result-line danger"><XCircle :size="15" />{{ item.rejectionReason }}</div>
    </article>
  </section>
</template>

<style scoped>
.task-tabs{grid-auto-flow:initial;grid-template-columns:repeat(4,1fr)}.task-tabs button{font-size:12px}.my-task{cursor:pointer}.result-line{margin-top:12px;padding-top:10px;display:flex;align-items:center;gap:6px;border-top:1px solid var(--line);font-size:12px}.result-line.warning{color:#b86c25}.result-line.success{color:#17845b}.result-line.danger{color:#c04444}
</style>
